import { supabase } from "@/integrations/supabase/client";
import type { Session } from "@supabase/supabase-js";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { toast } from "sonner";
import { fetchUserRole, type UserRoleResult } from "./fetchUserRole";
import { type TotpFactor, verifyTotpCode } from "./mfaFactors";
import { type ConfirmedMFAStatus, lookupMFAStatus, type MFAStatus } from "./mfaStatus";
import { getRedirectPathForRole } from "./redirectPathForRole";
import type { UserRoles } from "./userRoles";

interface UserProfile {
  id: string;
  display_name: string;
  display_color: string;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  session: Session | null;
  profile: UserProfile | null;
  role: UserRoles | null;
  roleResolved: boolean;
  loading: boolean;
  authStateReady: boolean;
  mfaRequired: boolean;
  mfaFactors: TotpFactor[];
  forceMFAEnrollment: boolean;
  isInviteOrRecoveryFlow: boolean;
  mfaRequiredForPasswordReset: boolean;
  mfaCheckFailed: boolean;
  redirectPathAfterLogin: string;
  navigateToAppHome: () => void;
  refreshRole: () => Promise<UserRoleResult>;
  clearForceMFAEnrollment: () => void;
  verifyMFA: (params: { factorId: string; code: string }) => Promise<{ error: string | null }>;
  checkMFAStatus: () => Promise<MFAStatus>;
  retryMFACheck: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  profile: null,
  role: null,
  roleResolved: false,
  loading: true,
  authStateReady: true,
  mfaRequired: false,
  mfaFactors: [],
  forceMFAEnrollment: false,
  isInviteOrRecoveryFlow: false,
  mfaRequiredForPasswordReset: false,
  mfaCheckFailed: false,
  redirectPathAfterLogin: "/admin/datasets",
  navigateToAppHome: () => {},
  refreshRole: async () => ({ kind: "error" }),
  clearForceMFAEnrollment: () => {},
  verifyMFA: async () => ({ error: null }),
  checkMFAStatus: async () => ({ status: "unavailable" }),
  retryMFACheck: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function isInviteOrRecoveryFromLocation(loc: { search: string; hash: string }): boolean {
  const params = new URLSearchParams(loc.search);
  let type = params.get("type");
  if (type === "invite" || type === "recovery") return true;
  if (loc.hash) {
    const hashWithoutLead = loc.hash.replace(/^#/, "");
    const firstSegment = hashWithoutLead.split("#")[0];
    const hashParams = new URLSearchParams(firstSegment.replace(/^[^?]*\?/, ""));
    type = hashParams.get("type");
    if (type === "invite" || type === "recovery") return true;
  }
  return false;
}

/**
 * Parse Supabase auth tokens from URL hash (recovery/invite redirect).
 * Hash format: #type=recovery#access_token=...&refresh_token=...&...
 */
export function getTokensFromAuthHash(hash: string): {
  access_token: string;
  refresh_token: string;
} | null {
  if (!hash?.includes("access_token=")) return null;
  const withoutLead = hash.replace(/^#/, "");
  const segments = withoutLead.split("#");
  const tokenSegment = segments.find((s) => s.includes("access_token="));
  if (!tokenSegment) return null;
  const params = new URLSearchParams(tokenSegment);
  const access_token = params.get("access_token");
  const refresh_token = params.get("refresh_token");
  if (access_token && refresh_token) return { access_token, refresh_token };
  return null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [role, setRole] = useState<UserRoles | null>(null);
  // Whether the role lookup returned a definitive answer (a role or a confirmed
  // zero-row). Stays false on fetch error/timeout so we don't treat a failed
  // lookup as "no role" and route the user to /no-role.
  const [roleResolved, setRoleResolved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [forceMFAEnrollment, setForceMFAEnrollment] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaFactors, setMfaFactors] = useState<TotpFactor[]>([]);
  const [mfaRequiredForPasswordReset, setMfaRequiredForPasswordReset] = useState(false);
  const [mfaCheckFailed, setMfaCheckFailed] = useState(false);
  const [authStateReady, setAuthStateReady] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const clearForceMFAEnrollment = useCallback(() => {
    setForceMFAEnrollment(false);
  }, []);

  // Apply a role-lookup result to state. Returns the resolved role (or null for a
  // confirmed no-role); leaves role untouched on error so a transient failure
  // never clobbers an existing role or masquerades as "no role".
  const applyRoleResult = useCallback((result: UserRoleResult): UserRoles | null => {
    if (result.kind === "error") {
      setRoleResolved(false);
      return null;
    }
    const nextRole = result.kind === "role" ? result.role : null;
    setRole(nextRole);
    setRoleResolved(true);
    return nextRole;
  }, []);

  const redirectPathAfterLogin = useMemo(() => getRedirectPathForRole(role) || "/admin/datasets", [role]);

  const navigateToAppHome = useCallback(() => {
    if (role) {
      navigate(getRedirectPathForRole(role));
      return;
    }
    if (session?.user?.id) {
      fetchUserRole(session.user.id).then((result) => {
        navigate(getRedirectPathForRole(applyRoleResult(result)));
      });
      return;
    }
    navigate("/admin/datasets");
  }, [navigate, role, session?.user?.id, applyRoleResult]);

  // Ref to latest session so getSession() callback can avoid stale redirects
  // and so we only redirect when we're still in a "no session" state.
  const sessionRef = useRef<Session | null>(session);
  sessionRef.current = session;

  const roleRef = useRef<UserRoles | null>(role);
  roleRef.current = role;

  const mfaCheckFailedRef = useRef(mfaCheckFailed);
  mfaCheckFailedRef.current = mfaCheckFailed;

  const latestMFARunRef = useRef(0);
  const abandonMFARuns = useCallback(() => {
    latestMFARunRef.current += 1;
    setAuthStateReady(true);
  }, []);

  // Re-fetch the current user's role and apply it to context state. Returns the
  // raw result so callers (e.g. the no-role page) can react to error vs no-role
  // vs role, and can rely on context being updated before they navigate.
  const refreshRole = useCallback(async (): Promise<UserRoleResult> => {
    const userId = sessionRef.current?.user?.id;
    if (!userId) {
      return { kind: "error" };
    }
    const result = await fetchUserRole(userId);
    applyRoleResult(result);
    return result;
  }, [applyRoleResult]);

  // Ref to latest location so auth callback can read current route when it runs.
  const locationRef = useRef(location);
  locationRef.current = location;

  // Run MFA block only once per session; skip duplicate SIGNED_IN/INITIAL_SESSION from resubscribe.
  const lastMfaSessionKeyRef = useRef<string | null>(null);

  // Helper: Is this an invite or recovery flow?
  const isInviteOrRecovery = useCallback(
    () => isInviteOrRecoveryFromLocation(location),
    [location.search, location.hash, location.pathname],
  );

  const isInviteOrRecoveryFlow = isInviteOrRecovery();

  const gotoToAuth = useCallback(
    (options?: { sessionExpired?: boolean }) => {
      const sessionExpired = options?.sessionExpired === true;
      navigate(sessionExpired ? `/auth?reason=session_expired${location.hash}` : `/auth${location.hash}`);
    },
    [navigate, location.hash],
  );

  const isAtAuth = useCallback(() => {
    return location.pathname === "/auth";
  }, [location.pathname]);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase.from("profiles").select("*").eq("id", userId).single();

      if (error) {
        console.error("Error fetching profile:", error);
        return;
      }

      setProfile(data);
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  const checkMFAStatus = useCallback(() => lookupMFAStatus(), []);

  const verifyMFA = useCallback(async (params: { factorId: string; code: string }) => {
    const { error } = await verifyTotpCode(params);
    if (error) return { error };

    setMfaRequired(false);
    setMfaFactors([]);

    return { error: null };
  }, []);

  // Build-time constant: import.meta.env.DEV is false in production/staging builds,
  // so this flag is a no-op even if VITE_DISABLE_MFA is accidentally set outside local dev.
  const mfaDisabled = import.meta.env.DEV && import.meta.env.VITE_DISABLE_MFA === "true";

  const startMFARun = useCallback(() => {
    latestMFARunRef.current += 1;
    const runId = latestMFARunRef.current;
    return () => runId === latestMFARunRef.current;
  }, []);

  const applySignInMFAResult = useCallback(
    (result: ConfirmedMFAStatus) => {
      const { factors, hasEnrolledFactors, hasCompletedMFA } = result;
      setMfaCheckFailed(false);
      setForceMFAEnrollment(!hasEnrolledFactors);
      if (locationRef.current.pathname !== "/auth" && (!hasEnrolledFactors || !hasCompletedMFA)) gotoToAuth();

      const needsCode = hasEnrolledFactors && !hasCompletedMFA;
      setMfaFactors(needsCode ? factors : []);
      setMfaRequired(needsCode);
    },
    [gotoToAuth],
  );

  const applyPasswordResetMFAResult = useCallback((result: ConfirmedMFAStatus) => {
    setMfaCheckFailed(false);
    setMfaRequiredForPasswordReset(result.hasEnrolledFactors && !result.hasCompletedMFA);
  }, []);

  const checkForForcedMFAEnrollment = useCallback(async () => {
    if (mfaDisabled) {
      setForceMFAEnrollment(false);
      return;
    }
    const runAtStart = latestMFARunRef.current;
    const result = await checkMFAStatus();
    if (runAtStart !== latestMFARunRef.current) return;
    if (result.status !== "ok") return;
    if (!mfaCheckFailedRef.current) {
      setForceMFAEnrollment(!result.hasEnrolledFactors);
      return;
    }

    if (isInviteOrRecoveryFromLocation(locationRef.current)) {
      setForceMFAEnrollment(!result.hasEnrolledFactors);
      applyPasswordResetMFAResult(result);
      return;
    }
    applySignInMFAResult(result);
  }, [checkMFAStatus, mfaDisabled, applySignInMFAResult, applyPasswordResetMFAResult]);

  const resolveSignInMFA = useCallback(async () => {
    const isCurrentRun = startMFARun();
    setAuthStateReady(false);
    const result = await checkMFAStatus();
    if (!isCurrentRun()) return;

    if (result.status === "ok") {
      applySignInMFAResult(result);
    } else {
      setMfaCheckFailed(true);
      setMfaRequired(false);
      setMfaFactors([]);
      if (locationRef.current.pathname !== "/auth") gotoToAuth();
    }
    setAuthStateReady(true);
  }, [checkMFAStatus, gotoToAuth, startMFARun, applySignInMFAResult]);

  const resolvePasswordResetMFA = useCallback(async () => {
    const isCurrentRun = startMFARun();
    setAuthStateReady(false);
    const result = await checkMFAStatus();
    if (!isCurrentRun()) return;

    if (result.status === "ok") {
      applyPasswordResetMFAResult(result);
    } else {
      setMfaCheckFailed(true);
    }
    setAuthStateReady(true);
  }, [checkMFAStatus, startMFARun, applyPasswordResetMFAResult]);

  const resolveMFAForLocation = useCallback(async () => {
    if (mfaDisabled) return;
    if (isInviteOrRecoveryFromLocation(locationRef.current)) {
      await resolvePasswordResetMFA();
      return;
    }
    await resolveSignInMFA();
  }, [mfaDisabled, resolvePasswordResetMFA, resolveSignInMFA]);

  const retryMFACheck = useCallback(async () => {
    if (!sessionRef.current) return;
    await resolveMFAForLocation();
  }, [resolveMFAForLocation]);

  useEffect(() => {
    if ((forceMFAEnrollment || mfaCheckFailed) && !isAtAuth()) {
      gotoToAuth();
    }
  }, [forceMFAEnrollment, mfaCheckFailed, gotoToAuth, isAtAuth]);

  // Helper functions for auth state decisions
  const shouldFetchRole = useCallback((event: string, pathname: string): boolean => {
    // Always fetch on INITIAL_SESSION and TOKEN_REFRESHED (normal flow)
    if (event === "INITIAL_SESSION" || event === "TOKEN_REFRESHED") {
      return true;
    }
    // For SIGNED_IN, only fetch if we're on / or /auth (first login scenario)
    // This prevents issues when SIGNED_IN fires on refresh of deep links
    if (event === "SIGNED_IN") {
      return pathname === "/" || pathname === "/auth";
    }
    return false;
  }, []);

  const shouldSetLoadingFalse = useCallback((event: string, pathname: string): boolean => {
    // Always set loading false on INITIAL_SESSION and TOKEN_REFRESHED
    if (event === "INITIAL_SESSION" || event === "TOKEN_REFRESHED") {
      return true;
    }
    // Set loading false on SIGNED_OUT
    if (event === "SIGNED_OUT") {
      return true;
    }
    // For SIGNED_IN, only set loading false if we're on / or /auth (first login)
    // On deep links, wait for INITIAL_SESSION to fire and fetch the role first
    if (event === "SIGNED_IN") {
      return pathname === "/" || pathname === "/auth";
    }
    return false;
  }, []);

  const shouldRedirectOnSignIn = useCallback((event: string, pathname: string): boolean => {
    // Only redirect on SIGNED_IN event
    if (event !== "SIGNED_IN") {
      return false;
    }
    // Only redirect from root or auth page, not from deep links. A null role is a
    // valid destination here (getRedirectPathForRole sends it to /no-role).
    return pathname === "/" || pathname === "/auth";
  }, []);

  // Main effect: Set up auth listener ONCE on mount
  useEffect(() => {
    // Initial getSession() on mount: ensures we exit loading when refresh failed (400) and no event fired
    const applyNoSession = () => {
      setSession(null);
      setRole(null);
      setProfile(null);
      setForceMFAEnrollment(false);
      setMfaRequired(false);
      setMfaFactors([]);
      setMfaCheckFailed(false);
      abandonMFARuns();
      setLoading(false);
      if (
        sessionRef.current === null &&
        locationRef.current.pathname !== "/auth" &&
        !locationRef.current.pathname.startsWith("/mfa")
      ) {
        toast.error("Session Expired", {
          description: "Please sign in again to continue.",
        });
        gotoToAuth({ sessionExpired: true });
      }
    };

    (async () => {
      // Recover session from URL hash (recovery/invite redirect). Supabase does not auto-parse the hash.
      if (locationRef.current.pathname === "/auth" && locationRef.current.hash) {
        const tokens = getTokensFromAuthHash(locationRef.current.hash);
        if (tokens) {
          const { error } = await supabase.auth.setSession(tokens);
          if (error) {
            console.error("[Auth] Recovery setSession failed:", error.message);
          }
        }
      }

      const { data, error } = await supabase.auth.getSession();
      if (error || !data.session) {
        if (sessionRef.current === null) {
          applyNoSession();
        }
      }
    })().catch(() => {
      if (sessionRef.current === null) {
        applyNoSession();
      }
    });

    // Safety net: if loading still true after 10s (e.g. client bug, missing event), set false so route effect can redirect
    const loadingTimeout = setTimeout(() => {
      setLoading((prev) => (prev ? false : prev));
    }, 10000);

    // Track tab visibility changes
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Tab became visible - check session and ensure loading is set correctly
        supabase.auth.getSession().then(({ data, error }) => {
          // If we have a session but loading is still true, set it to false
          // This handles the case where INITIAL_SESSION didn't fire after tab inactivity
          if (data.session && loading) {
            setLoading(false);
            // Also ensure we have the role if we don't have it yet
            if (data.session.user?.id && !role) {
              fetchUserRole(data.session.user.id).then(applyRoleResult);
            }
          }
          // If no session or error, clear loading so we're not stuck (e.g. refresh failed after tab inactive)
          if (!data.session || error) {
            setLoading(false);
          }
        });
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Listen for auth changes - INITIAL_SESSION will fire immediately with existing session
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // On SIGNED_OUT with null: clear session immediately so logout works (signOut()
      // may leave getSession() returning the old session briefly). Then verify with
      // getSession() and restore only if we still have a session (spurious event).
      if (!session && event === "SIGNED_OUT") {
        lastMfaSessionKeyRef.current = null;
        setSession(null);
        setRole(null);
        setProfile(null);
        setForceMFAEnrollment(false);
        setMfaRequired(false);
        setMfaFactors([]);
        setMfaCheckFailed(false);
        abandonMFARuns();
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          setSession(data.session);
          setLoading(false);
          if (data.session.user?.id) {
            fetchUserRole(data.session.user.id).then(applyRoleResult);
            fetchProfile(data.session.user.id);
            lastMfaSessionKeyRef.current = data.session.access_token;
            void resolveMFAForLocation();
          }
          return;
        }
        localStorage.removeItem("admin_customer_filter");
        localStorage.removeItem("skip_results_filter");
        toast.error("Session Expired", {
          description: "Please sign in again to continue.",
        });
        gotoToAuth({ sessionExpired: true });
        return;
      }

      // When tab regains focus Supabase often re-emits INITIAL_SESSION/TOKEN_REFRESHED.
      // If we already have this user and a role, only update session (tokens) and loading to avoid
      // redundant role/profile fetches and state storms that can feel like a refresh.
      if (
        (event === "INITIAL_SESSION" || event === "TOKEN_REFRESHED") &&
        session?.user?.id &&
        sessionRef.current?.user?.id === session.user.id &&
        roleRef.current != null
      ) {
        setSession(session);
        setLoading(false);
        return;
      }

      setSession(session);

      let fetchedUserRole: UserRoles | null = null;
      let roleWasResolved = false;
      const currentPathname = window.location.pathname;
      let needsRoleFetch = false;

      if (session?.user?.id) {
        // Use helper function to determine if we should fetch role
        needsRoleFetch = shouldFetchRole(event, currentPathname);

        if (needsRoleFetch) {
          try {
            // Securely fetch role from database instead of trusting unverified JWT
            // Add timeout to prevent hanging - critical for tab inactivity scenarios
            const roleFetchPromise = fetchUserRole(session.user.id);
            const timeoutPromise = new Promise<UserRoleResult>((resolve) => {
              setTimeout(() => {
                resolve({ kind: "error" });
              }, 3000); // 3 second timeout to prevent hanging
            });
            const result = await Promise.race([roleFetchPromise, timeoutPromise]);
            fetchedUserRole = applyRoleResult(result);
            roleWasResolved = result.kind !== "error";
          } catch (_error) {
            // Continue even if role fetch fails - don't block the auth flow
            setRoleResolved(false);
          }
        }
      } else {
        setRole(null);
        setRoleResolved(false);
      }

      const isInviteRecovery = isInviteOrRecoveryFromLocation(locationRef.current);
      const sessionKey = session?.access_token ?? null;
      const alreadyRanForSession = sessionKey != null && lastMfaSessionKeyRef.current === sessionKey;
      const shouldResolveSignInMFA =
        !mfaDisabled &&
        !!session &&
        (event === "SIGNED_IN" || event === "INITIAL_SESSION") &&
        !isInviteRecovery &&
        !alreadyRanForSession;

      if (session?.user) {
        fetchProfile(session.user.id);
        if (!shouldResolveSignInMFA) checkForForcedMFAEnrollment();
      } else {
        setProfile(null);
        setForceMFAEnrollment(false);
      }

      // Use helper function to determine if we should set loading false
      const needsLoadingFalse = shouldSetLoadingFalse(event, currentPathname);
      // When role fetch returned null (e.g. PGRST002), defer setLoading(false) and retry
      // regardless of pathname so a concurrent callback with path "/" can't set loading false
      // and trigger guard redirect.
      const roleFetchReturnedNull = needsLoadingFalse && fetchedUserRole === null && session?.user?.id;
      if (needsLoadingFalse) {
        if (roleFetchReturnedNull) {
          setTimeout(() => {
            fetchUserRole(session!.user!.id)
              .then((result) => {
                applyRoleResult(result);
                setLoading(false);
              })
              .catch(() => {
                setLoading(false);
              });
          }, 800);
        } else {
          setLoading(false);
        }
      } else if (session && event === "SIGNED_IN" && (currentPathname === "/" || currentPathname === "/auth")) {
        // Fallback: If SIGNED_IN at '/' or '/auth' but shouldSetLoadingFalse returned false,
        // still set loading to false. This is critical for tab inactivity scenarios where
        // INITIAL_SESSION might not fire reliably.
        // Set loading false regardless of role fetch status to prevent white screen
        setLoading(false);
      }

      // Skip the sign-in redirect during invite/recovery: the SIGNED_IN that setSession()
      // emits when we recover the session from the URL hash must leave the user on /auth
      // so they can set their password.
      const needsRedirect = shouldRedirectOnSignIn(event, currentPathname) && !isInviteRecovery;

      if (needsRedirect) {
        const cliReturnTo = sessionStorage.getItem("cli_auth_return_to");
        if (fetchedUserRole && cliReturnTo?.startsWith("/cli/authorize")) {
          sessionStorage.removeItem("cli_auth_return_to");
          navigate(cliReturnTo);
        } else if (fetchedUserRole || roleWasResolved) {
          // Only route once we have a definitive answer: a role, or a confirmed
          // no-role (→ /no-role). On error/timeout we leave the deferred retry to
          // resolve and let Index re-route, rather than flashing the no-role page.
          navigate(getRedirectPathForRole(fetchedUserRole));
        }
      }

      // Run only once per session to avoid duplicate runs when effect resubscribes (Supabase re-emits).
      if (shouldResolveSignInMFA) {
        lastMfaSessionKeyRef.current = sessionKey;
        void resolveSignInMFA();
      }
    });

    return () => {
      subscription.unsubscribe();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearTimeout(loadingTimeout);
    };
  }, [
    shouldFetchRole,
    shouldSetLoadingFalse,
    shouldRedirectOnSignIn,
    resolveSignInMFA,
    resolveMFAForLocation,
    gotoToAuth,
    applyRoleResult,
    abandonMFARuns,
  ]);

  // Separate effect: Monitor route changes for auth requirements (no-session, invite/recovery)
  // MFA-after-sign-in is handled in onAuthStateChange so it runs when the session is already applied.
  useEffect(() => {
    // Don't redirect while loading - wait for auth to complete
    if (loading) {
      return;
    }

    if (!session) {
      setMfaRequired(false);
      setMfaFactors([]);
      setMfaRequiredForPasswordReset(false);
      setMfaCheckFailed(false);
      abandonMFARuns();
      // Verify with Supabase: on connection loss or token refresh, React state
      // can be temporarily null while a valid session still exists in storage.
      supabase.auth.getSession().then(({ data, error }) => {
        if (data.session) {
          // Valid session in storage: restore it so we stay on the current page.
          setSession(data.session);
          setLoading(false);
          if (data.session.user?.id) {
            fetchUserRole(data.session.user.id).then(applyRoleResult);
            fetchProfile(data.session.user.id);
            lastMfaSessionKeyRef.current = data.session.access_token;
            void resolveMFAForLocation();
          }
          return;
        }
        // No session or error (e.g. refresh failed): ensure loading false and session null, then redirect if still in null state.
        if (!data.session || error) {
          setLoading(false);
          setSession(null);
          if (sessionRef.current === null && location.pathname !== "/auth" && !location.pathname.startsWith("/mfa")) {
            gotoToAuth({ sessionExpired: true });
          }
        }
      });
      return;
    }

    // When in invite/recovery with session, set MFA required for password reset
    if (session && isInviteOrRecovery()) {
      void resolvePasswordResetMFA();
    } else {
      setMfaRequiredForPasswordReset(false);
    }

    // Handle invite/recovery flows
    if (session && isInviteOrRecovery() && location.pathname !== "/auth") {
      gotoToAuth();
    }
  }, [
    session,
    location.pathname,
    isInviteOrRecovery,
    resolvePasswordResetMFA,
    abandonMFARuns,
    resolveMFAForLocation,
    gotoToAuth,
    location,
    loading,
    applyRoleResult,
  ]);

  // Only render children if not in invite/recovery flow and not forcing MFA
  const inFirstTimeSetup = isInviteOrRecovery();

  return (
    <AuthContext.Provider
      value={{
        session,
        profile,
        role,
        roleResolved,
        loading,
        authStateReady,
        mfaRequired,
        mfaFactors,
        forceMFAEnrollment,
        isInviteOrRecoveryFlow,
        mfaRequiredForPasswordReset,
        mfaCheckFailed,
        redirectPathAfterLogin,
        navigateToAppHome,
        refreshRole,
        clearForceMFAEnrollment,
        verifyMFA,
        checkMFAStatus,
        retryMFACheck,
      }}
    >
      {!loading && <>{(!inFirstTimeSetup || location.pathname === "/auth") && children}</>}
    </AuthContext.Provider>
  );
}
